import { FormEvent, useId, useState } from "react";
import { ConvexHttpClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";
import { ArrowRight, Check, LoaderCircle } from "lucide-react";
import { analytics } from "../lib/analytics";

type Status = "idle" | "loading" | "success" | "duplicate" | "error";
type JoinResult = { status: "created" | "existing" };

const joinWaitlist = makeFunctionReference<
  "mutation",
  { email: string; name: string; companyName: string; role: string; website: string; bottleneck?: string; source: string; consent: boolean; fax?: string },
  JoinResult
>("waitlist:join");

const roles = ["Founder / CEO", "Growth / Marketing", "Sales / Revenue", "Product", "Investor", "Other"];

export function WaitlistForm({ placement = "hero" }: { placement?: "hero" | "footer" }) {
  const id = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const convexUrl = import.meta.env.VITE_CONVEX_URL;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim().toLowerCase();
    const role = String(data.get("role") ?? "");
    const fax = String(data.get("fax") ?? "");

    if (fax) return; // Honeypot: bots receive no useful signal.
    if (!convexUrl) {
      setStatus("error");
      setMessage("Waitlist is warming up. Please try again shortly.");
      analytics.track({ name: "waitlist_failed", reason: "missing_backend" });
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      const client = new ConvexHttpClient(convexUrl);
      const result = await client.mutation(joinWaitlist, {
        email,
        role,
        name: String(data.get("name") ?? "").trim(),
        companyName: String(data.get("companyName") ?? "").trim(),
        website: String(data.get("website") ?? "").trim(),
        bottleneck: String(data.get("bottleneck") ?? "").trim() || undefined,
        source: `${placement}:${new URLSearchParams(window.location.search).get("utm_source") ?? "direct"}`,
        consent: data.get("consent") === "on",
        fax,
      });
      if (result.status === "existing") {
        setStatus("duplicate");
        setMessage("You’re already in. We’ll keep your place warm.");
        analytics.track({ name: "waitlist_duplicate" });
      } else {
        setStatus("success");
        setMessage("You’re in. Welcome to the founding cohort.");
        analytics.track({ name: "waitlist_submitted", role, source: placement });
        form.reset();
      }
    } catch (error) {
      setStatus("error");
      setMessage("That didn’t land. Check your connection and try once more.");
      analytics.track({ name: "waitlist_failed", reason: error instanceof Error ? error.name : "unknown" });
    }
  }

  const done = status === "success" || status === "duplicate";
  return (
    <form className="waitlist-form" onSubmit={submit} onFocus={() => analytics.track({ name: "waitlist_started", placement })}>
      <div className="form-grid">
        <label htmlFor={`${id}-name`}>
          <span>Your name</span>
          <input id={`${id}-name`} name="name" autoComplete="name" placeholder="Ada Lovelace" maxLength={80} required />
        </label>
        <label htmlFor={`${id}-companyName`}>
          <span>Company</span>
          <input id={`${id}-companyName`} name="companyName" autoComplete="organization" placeholder="Remarkable Labs" maxLength={120} required />
        </label>
        <label htmlFor={`${id}-email`}>
          <span>Work email</span>
          <input id={`${id}-email`} name="email" type="email" autoComplete="email" placeholder="ada@remarkable.co" required maxLength={254} />
        </label>
        <label htmlFor={`${id}-role`}>
          <span>I build distribution as</span>
          <select id={`${id}-role`} name="role" required defaultValue="">
            <option value="" disabled>Select your role</option>
            {roles.map((role) => <option key={role}>{role}</option>)}
          </select>
        </label>
        <label htmlFor={`${id}-website`}>
          <span>Company URL</span>
          <input id={`${id}-website`} name="website" type="url" inputMode="url" placeholder="https://yourcompany.com" maxLength={300} required />
        </label>
        <label className="form-wide" htmlFor={`${id}-bottleneck`}>
          <span>Biggest distribution bottleneck <em>optional</em></span>
          <input id={`${id}-bottleneck`} name="bottleneck" placeholder="Finding the right accounts…" maxLength={240} />
        </label>
      </div>
      <div className="honeypot" aria-hidden="true">
        <label htmlFor={`${id}-fax`}>Fax<input id={`${id}-fax`} name="fax" tabIndex={-1} autoComplete="off" /></label>
      </div>
      <label className="consent" htmlFor={`${id}-consent`}>
        <input id={`${id}-consent`} name="consent" type="checkbox" required />
        <span>I agree to receive beta updates. No spam, no sold data. Unsubscribe anytime.</span>
      </label>
      <button type="submit" disabled={status === "loading" || done}>
        {status === "loading" ? <><LoaderCircle className="spin" size={18} /> Requesting…</> : done ? <><Check size={18} /> You’re on the list</> : <>Request early access <ArrowRight size={18} /></>}
      </button>
      <p className={`form-message ${status}`} role="status" aria-live="polite">{message || "We’ll only use your details for beta access and product updates."}</p>
    </form>
  );
}
